import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { uploadProjectFile, ProjectFile } from '../../services/Project';
import { useI18n } from '../../i18n';
import {
  MAX_FILES_PER_UPLOAD,
  MAX_PROJECT_FILE_MB,
  UPLOAD_CONCURRENCY,
  runWithConcurrency,
  screenFiles,
  type SkipReason,
} from './project-uploads';

export type UploadStatus = 'waiting' | 'uploading' | 'uploaded' | 'failed';

export interface UploadEntry {
  key: string;
  name: string;
  size: number;
  status: UploadStatus;
  /** 0-100 */
  progress: number;
  error?: string;
}

export interface UploadOutcome {
  uploaded: ProjectFile[];
  failed: File[];
}

const NOTHING: UploadOutcome = { uploaded: [], failed: [] };

/** Names for a toast's description, cut short when there are many */
const listNames = (files: File[], max = 5): string => {
  const names = files.slice(0, max).map((f) => f.name);
  return files.length > max ? `${names.join(', ')} +${files.length - max}` : names.join(', ');
};

/**
 * Uploads a batch of files to a project, a few at a time, and keeps each
 * file's state for the progress dialog. One toast sums up the batch, with a
 * retry for the files that failed when the caller can take them again.
 */
export function useProjectFileUploads() {
  const { t } = useI18n();
  const [entries, setEntries] = useState<UploadEntry[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // State lags a render behind; this stops a second drop starting a batch alongside
  const busy = useRef(false);

  /** The files fit to upload; says in a toast which were skipped and why */
  const screen = useCallback(
    (files: ArrayLike<File>): File[] => {
      if (busy.current) {
        toast.info(t.projectUploads.toasts.busy);
        return [];
      }
      const { accepted, skipped } = screenFiles(files);
      const byReason = new Map<SkipReason, File[]>();
      for (const { file, reason } of skipped) {
        byReason.set(reason, [...(byReason.get(reason) ?? []), file]);
      }
      const titles: Record<SkipReason, string> = {
        type: t.projectUploads.toasts.skippedType,
        size: t.projectUploads.toasts.skippedSize(MAX_PROJECT_FILE_MB),
        empty: t.projectUploads.toasts.skippedEmpty,
        count: t.projectUploads.toasts.skippedCount(MAX_FILES_PER_UPLOAD),
      };
      byReason.forEach((skippedFiles, reason) => {
        toast.warning(titles[reason], { description: listNames(skippedFiles) });
      });
      return accepted;
    },
    [t],
  );

  /**
   * Uploads already-screened files. `onRetry` gets the failed files when the
   * user clicks Retry on the summary toast; leave it out where the caller
   * can't take them again (e.g. it navigates away).
   */
  const upload = useCallback(
    async (
      projectId: string,
      files: File[],
      options: { onRetry?: (failed: File[]) => void } = {},
    ): Promise<UploadOutcome> => {
      if (files.length === 0) return NOTHING;
      if (busy.current) {
        toast.info(t.projectUploads.toasts.busy);
        return NOTHING;
      }
      busy.current = true;

      const batchId = Date.now();
      const keys = files.map((_, i) => `${batchId}-${i}`);
      const patch = (key: string, changes: Partial<UploadEntry>) =>
        setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...changes } : e)));

      setEntries(
        files.map((file, i) => ({
          key: keys[i],
          name: file.name,
          size: file.size,
          status: 'waiting',
          progress: 0,
        })),
      );
      setIsUploading(true);

      try {
        type Result = { file: ProjectFile } | { error: string };
        const results = await runWithConcurrency<File, Result>(
          files,
          UPLOAD_CONCURRENCY,
          async (file, i) => {
            const key = keys[i];
            patch(key, { status: 'uploading' });
            let error = t.projectUploads.errors.generic;
            try {
              const res = await uploadProjectFile(projectId, file, (progress) =>
                patch(key, { progress }),
              );
              if (res.success && res.data) {
                patch(key, { status: 'uploaded', progress: 100 });
                return { file: res.data };
              }
              // nginx answers 413 itself, without our error body
              if (res.status === 413) {
                error = t.projectUploads.errors.tooLarge(MAX_PROJECT_FILE_MB);
              } else if (res.errors?.[0]) {
                error = res.errors[0].detail || res.errors[0].title || error;
              }
            } catch (err) {
              console.error('[useProjectFileUploads] Upload error:', err);
            }
            patch(key, { status: 'failed', error });
            return { error };
          },
        );

        const uploaded: ProjectFile[] = [];
        const failed: File[] = [];
        const errors: string[] = [];
        results.forEach((r, i) => {
          if ('file' in r) {
            uploaded.push(r.file);
          } else {
            failed.push(files[i]);
            errors.push(r.error);
          }
        });

        if (failed.length === 0) {
          toast.success(
            uploaded.length === 1
              ? t.projectUploads.toasts.uploadedOne(files[0].name)
              : t.projectUploads.toasts.uploadedMany(uploaded.length),
          );
        } else {
          const { onRetry } = options;
          toast.error(
            files.length === 1
              ? t.projectUploads.toasts.failedOne(failed[0].name)
              : t.projectUploads.toasts.failedSome(failed.length, files.length),
            {
              description: files.length === 1 ? errors[0] : listNames(failed),
              action: onRetry
                ? { label: t.common.actions.retry, onClick: () => onRetry(failed) }
                : undefined,
            },
          );
        }
        return { uploaded, failed };
      } finally {
        busy.current = false;
        setIsUploading(false);
      }
    },
    [t],
  );

  return { entries, isUploading, screen, upload };
}
